import * as logger from './logger';

const NAMESPACE_PREFIX = 'INV';
const DEFAULT_NAMESPACE = 'StorageService';
const DEFAULT_EXPIRATION_THRESHOLD = 24 * 60 * 60 * 1000; // 24 hours

type StorageNameSpace = `${typeof NAMESPACE_PREFIX}_${string}`;
type StorageKey = `${StorageNameSpace}_${string}`;

type TimestampedData<T> = {
  value: T;
  timestamp: number; // Represents the time the data was created or updated
};

/**
 * A generic storage service that supports both localStorage and sessionStorage.
 * Provides functionality to save, retrieve, and remove data with automatic cleanup of stale entries.
 *
 * @template T - The type of data to be stored.
 */
export class StorageService<T> {
  private storage: Storage;

  private namespace: StorageNameSpace;

  private autoCleanup: boolean;

  /**
   * Initializes the storage service.
   *
   * @param {boolean} [autoCleanup=false] - Whether to use TimestampedData functionality for automatic cleanup.
   * @param {number} [expirationThreshold=DEFAULT_EXPIRATION_THRESHOLD] - The time in milliseconds after which stale data is cleaned up.
   * @param {string} [namespace=DEFAULT_NAMESPACE] - A namespace to avoid conflicts with other storage items.
   * @param {boolean} [useSessionStorage=false] - Whether to use sessionStorage instead of localStorage.
   */
  constructor({
    autoCleanup = false,
    expirationThreshold = DEFAULT_EXPIRATION_THRESHOLD,
    namespace = DEFAULT_NAMESPACE,
    useSessionStorage = false
  }: {
    autoCleanup?: boolean;
    expirationThreshold?: number;
    namespace?: string;
    useSessionStorage?: boolean;
  } = {}) {
    this.storage = useSessionStorage ? sessionStorage : localStorage;
    this.namespace = `${NAMESPACE_PREFIX}_${namespace}`;
    this.autoCleanup = !useSessionStorage && autoCleanup; // Restrict autoCleanup to localStorage

    this.isStorageAvailable();

    if (this.autoCleanup) {
      this.cleanup(expirationThreshold); // Automatically clean up stale data
    }
  }

  private isStorageAvailable(): boolean {
    try {
      return typeof window !== 'undefined' && this.storage !== null;
    } catch (error) {
      logger.error('Storage is not available or accessible.', error);
      return false;
    }
  }

  private getNamespacedKey(key: string): StorageKey {
    return `${this.namespace}_${key}`;
  }

  private cleanup(expirationThreshold: number): void {
    const now = Date.now();

    Object.keys(this.storage).forEach((key) => {
      if (!key.startsWith(this.namespace)) return; // Skip non-namespaced keys

      try {
        const serializedData = this.storage.getItem(key);

        if (!serializedData) return;

        const timestampedData: TimestampedData<T> = JSON.parse(serializedData);

        if (
          timestampedData.timestamp &&
          now - timestampedData.timestamp > expirationThreshold
        ) {
          this.storage.removeItem(key);
        }
      } catch (error) {
        logger.warn(`Failed to parse or clean up data for key: ${key}`, error);
      }
    });
  }

  /**
   * Saves data to the storage.
   *
   * @param {string} key - The key under which the data is to be stored.
   * @param {T} data - The data to be stored.
   */
  save(key: string, data: T): void {
    try {
      const namespacedKey = this.getNamespacedKey(key);
      let serializedData: string;

      if (this.autoCleanup) {
        const timestampedData: TimestampedData<T> = {
          value: data,
          timestamp: Date.now()
        };

        serializedData = JSON.stringify(timestampedData);
      } else {
        serializedData = JSON.stringify(data);
      }

      this.storage.setItem(namespacedKey, serializedData);
    } catch (error) {
      logger.error(`Failed to save data into Storage for key: ${key}`, error);
    }
  }

  /**
   * Retrieves data from the storage.
   *
   * @param {string} key - The key of the data to be retrieved.
   * @returns {T | null} The retrieved data or null if not found.
   */
  retrieve(key: string): T | null {
    try {
      const namespacedKey = this.getNamespacedKey(key);

      const serializedData = this.storage.getItem(namespacedKey);

      if (!serializedData) return null;

      if (this.autoCleanup) {
        const timestampedData: TimestampedData<T> = JSON.parse(serializedData);

        return timestampedData.value;
      } else {
        return JSON.parse(serializedData);
      }
    } catch (error) {
      logger.error(
        `Failed to retrieve data from Storage for key: ${key}`,
        error
      );

      return null;
    }
  }

  /**
   * Removes data from the storage.
   *
   * @param {string} key - The key of the data to be removed.
   */
  remove(key: string): void {
    try {
      const namespacedKey = this.getNamespacedKey(key);

      this.storage.removeItem(namespacedKey);
    } catch (error) {
      logger.error(`Failed to remove data from Storage for key: ${key}`, error);
    }
  }
}
